import { FC } from 'react';

import { Col, Flex, Form, Input, Row, Select, SelectProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import useGenerationTypes from '@/hooks/useGenerationTypes';
import { PlantForm } from '@/types/plant';
import { normalizePositiveNumericValuesInput } from '@/utils';

type Props = {
  disabled?: boolean;
};

const MechanicalProps: FC<Props> = ({ disabled = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const generationTypes = useGenerationTypes();

  const flattenGenerationTypes = Object.values(
    generationTypes
  ) as SelectProps['options'];

  return (
    <Form.Item<PlantForm>
      wrapperCol={{ span: 24 }}
      className="p-16 pb-40"
      style={{
        border: '0.5px solid',
        borderRadius: '10px',
        borderColor: theme.colors.grayLightAccent,
      }}
    >
      <Col span={12}>
        <Form.Item<PlantForm>
          className="pr-8"
          name="generationType"
          label={t('plants.form.mechanicalProps.generationType.label')}
          getValueFromEvent={(value) => value?.value}
          // getValueFromEvent={(value) => ({
          //   value: value?.value,
          //   label: value?.label,
          // })}
          rules={[
            {
              required: true,
              message: t(
                'plants.form.mechanicalProps.generationType.requiredMessage'
              ),
            },
          ]}
        >
          <Select
            disabled={disabled}
            data-testid="generationType"
            labelInValue={true}
            labelRender={(option) => (
              <Flex gap={8} dir="row" align="center" justify="start">
                {
                  flattenGenerationTypes?.find(
                    (item) => item.value === option.value
                  )?.icon
                }
                {option.label}
              </Flex>
            )}
            options={flattenGenerationTypes}
            optionRender={(option) => (
              <Flex gap={8} dir="row" align="center" justify="start">
                {
                  flattenGenerationTypes?.find(
                    (item) => item.value === option.value
                  )?.icon
                }
                {option.label}
              </Flex>
            )}
            placeholder={t(
              'plants.form.mechanicalProps.generationType.placeholder'
            )}
          />
        </Form.Item>
      </Col>
      <Row>
        <Col className="pr-8" span={12}>
          <Form.Item<PlantForm>
            name="installedPowerKw"
            label={t('plants.form.mechanicalProps.installedPower.label')}
            hasFeedback
            normalize={normalizePositiveNumericValuesInput}
            rules={[
              {
                required: true,
                message: t(
                  'plants.form.mechanicalProps.installedPower.requiredMessage'
                ),
              },
            ]}
          >
            <Input
              disabled={disabled}
              addonAfter={
                <div>{t('plants.form.mechanicalProps.units.mw')}</div>
              }
              placeholder={t(
                'plants.form.mechanicalProps.installedPower.placeholder'
              )}
            />
          </Form.Item>
        </Col>
        <Col className="pl-8" span={12}>
          <Form.Item<PlantForm>
            name="licensedPowerKw"
            label={t('plants.form.mechanicalProps.licensedPower.label')}
            hasFeedback
            dependencies={['installedPowerKw']}
            normalize={normalizePositiveNumericValuesInput}
            rules={[
              {
                required: true,
                message: t(
                  'plants.form.mechanicalProps.licensedPower.requiredMessage'
                ),
              },
              {
                pattern: new RegExp(/^\d*\.?\d*$/),
                message: t(
                  'plants.form.mechanicalProps.licensedPower.patternMessage'
                ),
              },
              ({ getFieldValue }) => ({
                validator(_, value: string) {
                  const numberValue =
                    typeof value === 'string'
                      ? parseFloat(value.replace(/,/g, ''))
                      : parseFloat(value || 0);
                  const installedPower = getFieldValue('installedPowerKw');
                  if (!value || numberValue <= installedPower) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      t(
                        'plants.form.mechanicalProps.licensedPower.validationMessage'
                      )
                    )
                  );
                },
              }),
            ]}
          >
            <Input
              disabled={disabled}
              className="w-100"
              placeholder={t(
                'plants.form.mechanicalProps.licensedPower.placeholder'
              )}
              addonAfter={
                <div>{t('plants.form.mechanicalProps.units.mw')}</div>
              }
            />
          </Form.Item>
        </Col>
      </Row>
    </Form.Item>
  );
};

export default MechanicalProps;
